import React, { useState, useRef, useEffect } from 'react';
import { useTelegram } from '@/hooks';

interface OTPInputProps {
  length?: number;
  onComplete: (code: string) => void;
  isLoading?: boolean;
  error?: string | null;
  email?: string;
  onResend?: () => void;
}

export const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  onComplete,
  isLoading = false,
  error,
  email,
  onResend,
}) => {
  const [values, setValues] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { impactOccurred, selectionChanged } = useTelegram();

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);

    const newValues = [...values];
    newValues[index] = digit;
    setValues(newValues);

    selectionChanged();

    // Move to next input
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if complete
    if (newValues.every((v) => v) && newValues.join('').length === length) {
      impactOccurred('medium');
      onComplete(newValues.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !values[index] && index > 0) {
      // Move to previous input on backspace
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);

    if (pastedData.length > 0) {
      const newValues = Array(length).fill('');
      pastedData.split('').forEach((char, i) => {
        if (i < length) newValues[i] = char;
      });
      setValues(newValues);

      // Focus last filled or next empty
      const lastIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[lastIndex]?.focus();

      // Check if complete
      if (pastedData.length === length) {
        impactOccurred('medium');
        onComplete(pastedData);
      }
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {email && (
        <p
          style={{
            color: 'var(--tg-theme-hint-color)',
            fontSize: '14px',
            marginBottom: '24px',
            textAlign: 'center',
          }}
        >
          We sent a code to <strong style={{ color: 'var(--tg-theme-text-color)' }}>{email}</strong>
        </p>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '16px',
        }}
        onPaste={handlePaste}
      >
        {values.map((value, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={value}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={isLoading}
            style={{
              width: '44px',
              height: '52px',
              fontSize: '24px',
              fontWeight: 600,
              textAlign: 'center',
              border: error ? '2px solid #ff3b30' : '2px solid var(--tg-theme-hint-color)',
              borderRadius: '12px',
              backgroundColor: 'var(--tg-theme-secondary-bg-color)',
              color: 'var(--tg-theme-text-color)',
              outline: 'none',
            }}
          />
        ))}
      </div>

      {error && (
        <p
          style={{
            color: '#ff3b30',
            fontSize: '14px',
            textAlign: 'center',
            marginBottom: '16px',
          }}
        >
          {error}
        </p>
      )}

      {isLoading && (
        <p
          style={{
            color: 'var(--tg-theme-hint-color)',
            fontSize: '14px',
            textAlign: 'center',
          }}
        >
          Verifying...
        </p>
      )}

      {onResend && !isLoading && (
        <button
          onClick={() => {
            impactOccurred('light');
            onResend();
          }}
          style={{
            display: 'block',
            margin: '24px auto 0',
            background: 'none',
            border: 'none',
            color: 'var(--tg-theme-link-color)',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '8px 16px',
          }}
        >
          Resend Code
        </button>
      )}
    </div>
  );
};
